import { jwtDecode } from "jwt-decode";
import type { NotificationSilencingStatus } from "../models/be/room";

/**
 * Riassume lo stato corrente delle categorie di notifica.
 */
export function getNotificationSilencingStatus(silencedCategories: boolean[]): NotificationSilencingStatus {
    if (!silencedCategories.some(Boolean)) {
        return 'active';
    }

    return silencedCategories.every(Boolean) ? 'silenced' : 'partial';
}

/**
 * Verifica se l'orario fornito rientra in una qualsiasi delle fasce orarie dell'array.
 * Gestisce correttamente le fasce normali e quelle che superano la mezzanotte.
 *
 * @param slots - L'array di fasce orarie.
 * @returns `true` se l'ora è all'interno di una fascia, altrimenti `false`.
 */
export function isActualTimeInSlots(slots: {start_time: string, end_time: string}[]): boolean {
    const timeToCheck = new Date();
    // 1. Convertiamo l'ora attuale in minuti totali dalla mezzanotte.
    const currentTimeInMinutes = timeToCheck.getHours() * 60 + timeToCheck.getMinutes();

    // 2. Iteriamo su ogni fascia oraria per controllarla.
    for (const slot of slots) {
        const startMinutes = timeToMinutes(slot.start_time);
        const endMinutes = timeToMinutes(slot.end_time);

        // 3. Applichiamo la logica di controllo appropriata.
        let isInSlot = false;

        if (startMinutes <= endMinutes) {
            // Caso A: Fascia normale (es. 09:00 - 17:00)
            // L'ora deve essere compresa tra l'inizio e la fine.
            if (currentTimeInMinutes >= startMinutes && currentTimeInMinutes <= endMinutes) {
                isInSlot = true;
            }
        } else {
            // Caso B: Fascia "overnight" che supera la mezzanotte (es. 22:00 - 05:00)
            // L'ora deve essere o dopo l'inizio (es. 23:00) OPPURE prima della fine (es. 02:00).
            if (currentTimeInMinutes >= startMinutes || currentTimeInMinutes <= endMinutes) {
                isInSlot = true;
            }
        }

        // Se troviamo una corrispondenza, possiamo fermarci e restituire true.
        if (isInSlot) {
            return true;
        }
    }

    // Se il ciclo finisce, significa che l'ora non rientra in nessuna fascia.
    return false;
}


const timeToMinutes = (timeString: string): number => {
    // Gestiamo "24:00" come caso speciale per la fine del giorno
    if (timeString === "24:00") return 24 * 60;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
};

/**
 * Funzione interna che controlla sovrapposizioni su un set di fasce orarie semplici (nello stesso giorno).
 * (Questa è la funzione della risposta precedente, ora usata come helper)
 * @param slots - Array di fasce orarie semplici.
 * @returns true se ci sono sovrapposizioni, altrimenti false.
 */
export function checkSimpleOverlaps(slots: {start_time: string, end_time: string}[]): boolean {
    if (slots.length < 2) {
        return false;
    }

    const sortedSlots = [...slots].sort((a, b) =>
        timeToMinutes(a.start_time) - timeToMinutes(b.start_time)
    );

    for (let i = 0; i < sortedSlots.length - 1; i++) {
        const currentSlotEnd = timeToMinutes(sortedSlots[i].end_time);
        const nextSlotStart = timeToMinutes(sortedSlots[i + 1].start_time);

        if (currentSlotEnd > nextSlotStart) {
            return true;
        }
    }

    return false;
}

/**
 * Funzione principale che gestisce anche le fasce orarie che superano la mezzanotte.
 * @param slots - L'array originale di fasce orarie.
 * @returns `true` se esiste almeno una sovrapposizione, altrimenti `false`.
 */
export function checkForOverlapsAdvanced(slots: {start_time: string, end_time: string}[]): boolean {
    const processedSlots: {start_time: string, end_time: string}[] = [];

    // 1. Fase di elaborazione: spezza le fasce "overnight"
    for (const slot of slots) {
        const startMinutes = timeToMinutes(slot.start_time);
        const endMinutes = timeToMinutes(slot.end_time);

        if (startMinutes >= endMinutes) {
            // Questa fascia attraversa la mezzanotte (o è di durata zero/invalida)
            // La spezziamo in due. Es: 22:00 - 05:00 diventa:
            // 1. 22:00 - 24:00
            processedSlots.push({ start_time: slot.start_time, end_time: "24:00" });
            // 2. 00:00 - 05:00
            processedSlots.push({ start_time: "00:00", end_time: slot.end_time });
        } else {
            // Fascia normale, la aggiungiamo così com'è.
            processedSlots.push(slot);
        }
    }

    // 2. Fase di controllo: usiamo la funzione precedente sul nuovo array di fasce semplici.
    return checkSimpleOverlaps(processedSlots);
}


export function isAccessTokenValid(): boolean {
    const token = localStorage.getItem("ACCESS_TOKEN");
    if (!token) return false;
    const decodedToken = jwtDecode(token);
    if (Date.now() >= decodedToken.exp! * 1000) {
        return false;
    }
    return true;
}